import { ChevronRight, ExternalLink, LoaderCircle, Send, Trash2, UserRound } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { FormMessage } from "@/components/ui/form-message";
import { Panel } from "@/components/ui/panel";
import {
  getPqrCategoryTranslationKey,
  getPqrStatusTranslationKey,
} from "@/features/pqr/pqrMappers";
import { StatusBadge } from "@/features/pqr/components/StatusBadge";
import {
  pqrStatuses,
  type PqrAttachmentLink,
  type PqrRequestDetails,
  type PqrStatus,
  type PqrThreadMessage,
} from "@/features/pqr/pqr.types";

type PqrAdminReplyControls = {
  busy?: boolean;
  canReply: boolean;
  disabledReason?: string | null;
  draft: string;
  error?: string | null;
  onDraftChange: (value: string) => void;
  onSubmit: () => void;
};

type PQRRequestDetailsProps = {
  adminReply?: PqrAdminReplyControls;
  attachmentLinks: PqrAttachmentLink[];
  details: PqrRequestDetails | null;
  emptyBody: string;
  errorMessage?: string | null;
  isLoading?: boolean;
  onDelete?: () => void;
  onOpenUserProfile?: () => void;
  onStatusChange?: (status: PqrStatus) => void;
  statusActionBusy?: boolean;
  threadMessages?: PqrThreadMessage[];
  title: string;
  variant?: "panel" | "sheet";
};

export function PQRRequestDetails(props: PQRRequestDetailsProps) {
  const { variant = "panel" } = props;

  if (variant === "sheet") {
    return <SheetRequestDetails {...props} />;
  }

  return <PanelRequestDetails {...props} />;
}

function SheetRequestDetails({
  attachmentLinks,
  details,
  emptyBody,
  errorMessage,
  isLoading = false,
  threadMessages = [],
  title,
}: PQRRequestDetailsProps) {
  const { i18n, t } = useTranslation();
  const dateFormatter = new Intl.DateTimeFormat(i18n.language, {
    dateStyle: "medium",
    timeStyle: "short",
  });

  if (isLoading) {
    return (
      <div className="flex min-h-[40rem] items-center justify-center px-8 py-10">
        <div className="flex items-center gap-3 text-sm text-ink/70">
          <LoaderCircle className="size-4 animate-spin" />
          {t("pqr.details.loading")}
        </div>
      </div>
    );
  }

  if (errorMessage) {
    return (
      <div className="flex min-h-[40rem] items-center justify-center px-8 py-10">
        <p className="max-w-lg text-center text-sm leading-7 text-clay">{errorMessage}</p>
      </div>
    );
  }

  if (!details) {
    return (
      <div className="flex min-h-[40rem] items-center justify-center px-8 py-10">
        <div className="max-w-lg space-y-3 text-center">
          <p className="text-sm font-semibold uppercase tracking-[0.2em] text-clay">{title}</p>
          <p className="text-sm leading-7 text-ink/62">{emptyBody}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-full flex-col px-8 py-8">
      <div className="space-y-4">
        <div className="space-y-3">
          <h2 className="text-[2.35rem] font-semibold leading-tight text-ink">{details.subject}</h2>
          <StatusBadge status={details.status} />
        </div>

        <div className="grid gap-x-10 gap-y-8 border-b border-[#ece2d6] pb-8 md:grid-cols-2">
          <SheetDetailBlock
            label={t("pqr.details.ticketRef")}
            value={details.ticketRef}
          />
          <SheetDetailBlock
            label={t("pqr.details.submittedAt")}
            value={dateFormatter.format(new Date(details.createdAt))}
          />
          <SheetDetailBlock
            label={t("pqr.details.requester")}
            value={details.name}
          />
          <SheetDetailBlock
            label={t("pqr.details.email")}
            value={details.email}
          />
        </div>
      </div>

      <div className="space-y-10 py-8">
        <section className="border-b border-[#ece2d6] pb-8">
          <p className="text-sm font-semibold uppercase tracking-[0.18em] text-ink/48">
            {t("pqr.details.statusHistory", { defaultValue: "Status history" })}
          </p>

          <div className="mt-5 space-y-4">
            {details.statusHistory.length ? (
              details.statusHistory.map((entry, index) => (
                <article key={`${entry.changedAt}-${entry.status}-${index}`} className="flex gap-3">
                  <ChevronRight className="mt-1 size-4 shrink-0 text-ink/42" />
                  <div className="space-y-1">
                    <p className="text-[1.05rem] text-ink">
                      {entry.note
                        ?? t(getPqrStatusTranslationKey(entry.status))}
                    </p>
                    <p className="text-sm text-ink/52">
                      {dateFormatter.format(new Date(entry.changedAt))}
                      {" • "}
                      {entry.changedBy
                        ? t("pqr.details.recordedByAdmin", { defaultValue: "Recorded by admin" })
                        : t("pqr.details.recordedBySystem", { defaultValue: "Recorded by system" })}
                    </p>
                  </div>
                </article>
              ))
            ) : (
              <p className="mt-5 text-sm text-ink/58">
                {t("pqr.details.noStatusHistory", { defaultValue: "No status changes recorded yet." })}
              </p>
            )}
          </div>
        </section>

        <section className="border-b border-[#ece2d6] pb-8">
          <p className="text-sm font-semibold uppercase tracking-[0.18em] text-ink/48">
            {t("pqr.details.message")}
          </p>
          <p className="mt-6 whitespace-pre-wrap text-[1.05rem] leading-8 text-ink/84">
            {details.message}
          </p>
        </section>

        <section className="border-b border-[#ece2d6] pb-8">
          <p className="text-sm font-semibold uppercase tracking-[0.18em] text-ink/48">
            {t("pqr.thread.title")}
          </p>

          <div className="mt-6 space-y-5">
            {threadMessages.length ? (
              threadMessages.map((threadMessage) => (
                <article key={threadMessage.id} className="space-y-3">
                  <div className="flex flex-wrap items-center gap-4 text-sm">
                    <p className="font-semibold text-ink">{t("pqr.thread.supportTeam")}</p>
                    <p className="text-ink/52">
                      {dateFormatter.format(new Date(threadMessage.createdAt))}
                    </p>
                  </div>
                  <div className="border-l-2 border-[#e7ddd0] pl-4 text-[1.02rem] leading-8 text-ink/76">
                    {threadMessage.body}
                  </div>
                </article>
              ))
            ) : (
              <p className="text-sm text-ink/58">{t("pqr.thread.empty")}</p>
            )}
          </div>
        </section>

        <section>
          <p className="text-sm font-semibold uppercase tracking-[0.18em] text-ink/48">
            {t("pqr.details.attachments")}
          </p>

          <div className="mt-6 space-y-4">
            {attachmentLinks.length ? (
              attachmentLinks.map((attachment) => (
                <a
                  className="flex items-center justify-between gap-4 rounded-[1.25rem] border border-[#ece2d6] bg-[#fdfaf5] px-4 py-4 transition hover:bg-white"
                  href={attachment.signedUrl}
                  key={attachment.path}
                  rel="noreferrer"
                  target="_blank"
                >
                  <div className="min-w-0">
                    <p className="truncate text-[1.05rem] font-semibold text-ink">{attachment.fileName}</p>
                    <p className="mt-1 truncate text-sm text-ink/48">{attachment.path}</p>
                  </div>
                  <span className="inline-flex items-center gap-2 text-sm font-semibold text-ink/66">
                    {t("pqr.details.downloadAttachment")}
                    <ExternalLink className="size-4" />
                  </span>
                </a>
              ))
            ) : (
              <p className="text-sm text-ink/58">{t("pqr.details.noAttachments")}</p>
            )}
          </div>
        </section>
      </div>
    </div>
  );
}

function PanelRequestDetails({
  adminReply,
  attachmentLinks,
  details,
  emptyBody,
  errorMessage,
  isLoading = false,
  onDelete,
  onOpenUserProfile,
  onStatusChange,
  statusActionBusy = false,
  threadMessages = [],
  title,
}: PQRRequestDetailsProps) {
  const { i18n, t } = useTranslation();
  const dateFormatter = new Intl.DateTimeFormat(i18n.language, {
    dateStyle: "medium",
    timeStyle: "short",
  });

  if (isLoading) {
    return (
      <Panel className="flex min-h-[24rem] items-center justify-center">
        <div className="flex items-center gap-3 text-sm text-ink/70">
          <LoaderCircle className="size-4 animate-spin" />
          {t("pqr.details.loading")}
        </div>
      </Panel>
    );
  }

  if (errorMessage) {
    return (
      <Panel className="flex min-h-[24rem] items-center justify-center">
        <p className="max-w-md text-center text-sm leading-7 text-clay">{errorMessage}</p>
      </Panel>
    );
  }

  if (!details) {
    return (
      <Panel className="flex min-h-[24rem] items-center justify-center">
        <div className="max-w-md space-y-3 text-center">
          <p className="text-sm font-semibold uppercase tracking-[0.22em] text-clay">{title}</p>
          <p className="text-sm leading-7 text-ink/65">{emptyBody}</p>
        </div>
      </Panel>
    );
  }

  return (
    <Panel className="space-y-6">
      <div className="flex flex-col gap-4 xl:flex-row xl:items-start xl:justify-between">
        <div className="space-y-3">
          <p className="text-sm font-semibold uppercase tracking-[0.22em] text-clay">{title}</p>
          <div className="space-y-2">
            <h2 className="font-display text-3xl text-ink">{details.subject}</h2>
            <div className="flex flex-wrap items-center gap-2">
              <Badge variant="outline">{t(getPqrCategoryTranslationKey(details.category))}</Badge>
              <StatusBadge status={details.status} />
            </div>
          </div>
        </div>

        {onStatusChange || onDelete || (details.userId && onOpenUserProfile) ? (
          <div className="flex flex-wrap items-end gap-3">
            {details.userId && onOpenUserProfile ? (
              <Button
                type="button"
                variant="outline"
                onClick={onOpenUserProfile}
              >
                <UserRound className="size-4" />
                Open user profile
              </Button>
            ) : null}
            {onStatusChange ? (
              <label className="space-y-2">
                <span className="text-xs font-semibold uppercase tracking-[0.18em] text-ink/55">
                  {t("pqr.admin.statusLabel")}
                </span>
                <select
                  aria-label={t("pqr.admin.statusLabel")}
                  className="min-w-44 rounded-full border border-ink/10 bg-white/80 px-4 py-2 text-sm text-ink shadow-sm outline-none transition focus:border-clay/50 focus:ring-2 focus:ring-clay/20"
                  disabled={statusActionBusy}
                  value={details.status}
                  onChange={(event) => {
                    const nextStatus = event.target.value as PqrStatus;
                    if (nextStatus !== details.status) {
                      onStatusChange(nextStatus);
                    }
                  }}
                >
                  {pqrStatuses.map((status) => (
                    <option key={status} value={status}>
                      {t(getPqrStatusTranslationKey(status))}
                    </option>
                  ))}
                </select>
              </label>
            ) : null}

            {onDelete ? (
              <Button
                disabled={statusActionBusy}
                type="button"
                variant="destructive"
                onClick={onDelete}
              >
                <Trash2 className="size-4" />
                {t("pqr.admin.deleteAction")}
              </Button>
            ) : null}
          </div>
        ) : null}
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <DetailBlock
          label={t("pqr.details.ticketRef")}
          value={details.ticketRef}
        />
        <DetailBlock
          label={t("pqr.details.submittedAt")}
          value={dateFormatter.format(new Date(details.createdAt))}
        />
        <DetailBlock
          label={t("pqr.details.requester")}
          value={details.name}
        />
        <DetailBlock
          label={t("pqr.details.email")}
          value={details.email}
        />
      </div>

      <section className="space-y-3">
        <p className="text-xs font-semibold uppercase tracking-[0.18em] text-clay">
          {t("pqr.details.statusHistory", { defaultValue: "Status history" })}
        </p>
        {details.statusHistory.length ? (
          <div className="space-y-3">
            {details.statusHistory.map((entry, index) => (
              <article
                className="rounded-[1.5rem] border border-white/70 bg-white/72 px-5 py-4 shadow-vellum"
                key={`${entry.changedAt}-${entry.status}-${index}`}
              >
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div className="flex flex-wrap items-center gap-2">
                    <StatusBadge status={entry.status} />
                    <p className="text-xs uppercase tracking-[0.16em] text-ink/50">
                      {entry.changedBy
                        ? t("pqr.details.recordedByAdmin", { defaultValue: "Recorded by admin" })
                        : t("pqr.details.recordedBySystem", { defaultValue: "Recorded by system" })}
                    </p>
                  </div>
                  <p className="text-xs uppercase tracking-[0.16em] text-ink/50">
                    {dateFormatter.format(new Date(entry.changedAt))}
                  </p>
                </div>
                {entry.note ? (
                  <p className="mt-3 whitespace-pre-wrap text-sm leading-7 text-ink/76">{entry.note}</p>
                ) : (
                  <p className="mt-3 text-sm leading-7 text-ink/58">
                    {t(getPqrStatusTranslationKey(entry.status))}
                  </p>
                )}
              </article>
            ))}
          </div>
        ) : (
          <div className="rounded-[1.5rem] border border-dashed border-ink/15 bg-white/55 px-5 py-6 text-sm text-ink/60">
            {t("pqr.details.noStatusHistory", { defaultValue: "No status changes recorded yet." })}
          </div>
        )}
      </section>

      <section className="space-y-3">
        <p className="text-xs font-semibold uppercase tracking-[0.18em] text-clay">
          {t("pqr.details.message")}
        </p>
        <div className="rounded-[1.75rem] border border-white/70 bg-white/72 px-5 py-5 shadow-vellum">
          <p className="whitespace-pre-wrap text-sm leading-7 text-ink/78">{details.message}</p>
        </div>
      </section>

      <section className="space-y-3">
        <p className="text-xs font-semibold uppercase tracking-[0.18em] text-clay">
          {t("pqr.thread.title")}
        </p>
        {threadMessages.length ? (
          <div className="space-y-3">
            {threadMessages.map((threadMessage) => (
              <article
                className="rounded-[1.5rem] border border-white/70 bg-white/72 px-5 py-4 shadow-vellum"
                key={threadMessage.id}
              >
                <div className="flex flex-wrap items-center gap-3">
                  <Badge variant="secondary">{t("pqr.thread.supportTeam")}</Badge>
                  <p className="text-xs uppercase tracking-[0.16em] text-ink/50">
                    {dateFormatter.format(new Date(threadMessage.createdAt))}
                  </p>
                </div>
                <p className="mt-3 whitespace-pre-wrap text-sm leading-7 text-ink/78">
                  {threadMessage.body}
                </p>
              </article>
            ))}
          </div>
        ) : (
          <div className="rounded-[1.5rem] border border-dashed border-ink/15 bg-white/55 px-5 py-6 text-sm text-ink/60">
            {t("pqr.thread.empty")}
          </div>
        )}
      </section>

      {adminReply ? (
        <section className="space-y-3">
          <div className="space-y-1">
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-clay">
              {t("pqr.thread.replyLabel")}
            </p>
            <p className="text-sm text-ink/60">
              {adminReply.canReply
                ? t("pqr.thread.replyHelp")
                : adminReply.disabledReason ?? t("pqr.thread.replyDisabled")}
            </p>
          </div>

          {adminReply.canReply ? (
            <div className="space-y-3">
              <label className="sr-only" htmlFor="pqr-admin-reply">
                {t("pqr.thread.replyLabel")}
              </label>
              <textarea
                id="pqr-admin-reply"
                className="min-h-32 w-full rounded-[1.75rem] border border-ink/10 bg-white/80 px-4 py-3 text-sm text-ink shadow-sm outline-none transition focus:border-clay/50 focus:ring-2 focus:ring-clay/20 disabled:cursor-not-allowed disabled:opacity-70"
                disabled={adminReply.busy}
                placeholder={t("pqr.thread.replyPlaceholder")}
                value={adminReply.draft}
                onChange={(event) => adminReply.onDraftChange(event.target.value)}
              />
              {adminReply.error ? <FormMessage>{adminReply.error}</FormMessage> : null}
              <div className="flex justify-end">
                <Button
                  disabled={adminReply.busy || adminReply.draft.trim().length === 0}
                  type="button"
                  onClick={adminReply.onSubmit}
                >
                  <Send className="size-4" />
                  {t("pqr.thread.replyAction")}
                </Button>
              </div>
            </div>
          ) : (
            <div className="rounded-[1.5rem] border border-dashed border-ink/15 bg-white/55 px-5 py-6 text-sm text-ink/60">
              {adminReply.disabledReason ?? t("pqr.thread.replyDisabled")}
            </div>
          )}
        </section>
      ) : null}

      <section className="space-y-3">
        <p className="text-xs font-semibold uppercase tracking-[0.18em] text-clay">
          {t("pqr.details.attachments")}
        </p>
        {attachmentLinks.length ? (
          <div className="space-y-3">
            {attachmentLinks.map((attachment) => (
              <a
                className="flex items-center justify-between gap-4 rounded-[1.5rem] border border-white/70 bg-white/72 px-5 py-4 text-sm text-ink shadow-vellum transition hover:bg-white"
                href={attachment.signedUrl}
                key={attachment.path}
                rel="noreferrer"
                target="_blank"
              >
                <div className="min-w-0">
                  <p className="truncate font-semibold text-ink">{attachment.fileName}</p>
                  <p className="truncate text-xs uppercase tracking-[0.16em] text-ink/50">
                    {attachment.path}
                  </p>
                </div>
                <span className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.18em] text-clay">
                  {t("pqr.details.downloadAttachment")}
                  <ExternalLink className="size-4" />
                </span>
              </a>
            ))}
          </div>
        ) : (
          <div className="rounded-[1.5rem] border border-dashed border-ink/15 bg-white/55 px-5 py-6 text-sm text-ink/60">
            {t("pqr.details.noAttachments")}
          </div>
        )}
      </section>
    </Panel>
  );
}

function SheetDetailBlock({ label, value }: { label: string; value: string }) {
  return (
    <div className="space-y-2">
      <p className="text-sm font-semibold uppercase tracking-[0.18em] text-ink/48">{label}</p>
      <p className="break-words text-[1.05rem] text-ink">{value}</p>
    </div>
  );
}

function DetailBlock({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-[1.5rem] border border-white/70 bg-white/72 px-5 py-4 shadow-vellum">
      <p className="text-xs font-semibold uppercase tracking-[0.18em] text-clay">{label}</p>
      <p className="mt-2 break-words text-sm text-ink/78">{value}</p>
    </div>
  );
}
